import { filter, get, isEmpty, keys } from "lodash";
import { Flex } from "orcs-design-system";
import PropTypes from "prop-types";
import React, { useMemo } from "react";

import TagSelectorContainer from "src/components/TagsEditor/sub-components/TagSelectorContainer";
import TagsEditor from "src/components/TagsEditor/TagsEditor";
import { ENTITY_TYPES } from "src/consts/entityTypes";

import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import useHandleEntityTagClick from "src/hooks/useHandleEntityTagClick";
import { getHighlightedTags } from "../HighlightedTags/utils";

const configKeyName = {
  [ENTITY_TYPES.GROUP]: "isGroupBadge",
  [ENTITY_TYPES.PERSON]: "isPersonBadge",
};

const TagDisplay = ({
  tags = [],
  entityType = /** @type {string} */ (ENTITY_TYPES.GROUP),
  showHighlightedTagsOnly = false,
  excludeAssociationTags = true,
  onEntityTagClick, // Optional custom click handler
}) => {
  const workspace = useWorkspace();
  const defaultHandleTagClick = useHandleEntityTagClick();
  const handleTagClick = onEntityTagClick || defaultHandleTagClick;
  const tagConfig = get(workspace, "config.tagConfig", {});

  const assocTagTypes = useMemo(
    () =>
      new Set(
        filter(
          keys(tagConfig),
          (tagConfigKey) => tagConfig[tagConfigKey]?.isGroupAssociation
        )
      ),
    [tagConfig]
  );

  const filteredTags = showHighlightedTagsOnly
    ? getHighlightedTags(tags, tagConfig, configKeyName[entityType])
    : tags;

  const finalTags = useMemo(
    () =>
      excludeAssociationTags
        ? filter(filteredTags, (tag) => !assocTagTypes.has(tag.type))
        : filteredTags,
    [filteredTags, excludeAssociationTags, assocTagTypes]
  );

  if (isEmpty(finalTags)) {
    return null;
  }

  return (
    <Flex alignItems="center" flexWrap="wrap">
      <TagsEditor
        readOnly
        tagTypesConfig={tagConfig}
        existingTags={finalTags}
        onEntityTagClick={handleTagClick}
        setNewTag={async () => {}}
        removeTag={async () => {}}
        components={{ TagSelector: TagSelectorContainer }}
        small
        mt="xs"
        entityType={entityType}
      />
    </Flex>
  );
};

TagDisplay.propTypes = {
  tags: PropTypes.arrayOf(PropTypes.object),
  entityType: PropTypes.string,
  showHighlightedTagsOnly: PropTypes.bool,
  excludeAssociationTags: PropTypes.bool,
  onEntityTagClick: PropTypes.func, // Optional custom click handler
};

export default TagDisplay;
